import { DocumentReference } from "@angular/fire/firestore";
import { ProductType } from "./enums/product-type.enum";
import { ScheduleType } from "./enums/schedule-type.enum";
import { ProductStatus } from "./enums/product-status.enum";
import { Observable, Subscription } from "rxjs";
import { CalendarEvent } from "../models/calendar-event";
import { Timeframe } from "../models/timeframe";
import { DateService } from "../services/date.service";
import { BasicQuestion } from "../models/ingredients";
import { ProductSharedSettings } from "./models/product.shared";
import { ProductAdvancedSettings } from "./models/product.advanced";
import { ProductScheduleSettings } from "./models/product.schedule";
import { ProductInventorySettings } from "./models/product.inventory";
import { ProductBuilder } from "./product.builder";
import { RedeemType } from "./enums/redeem-type.enum";
import * as firebase from "firebase";
export class Product {
  private readonly ds: DateService = new DateService();

  private readonly _shared: ProductSharedSettings;
  private readonly _advanced: ProductAdvancedSettings;
  private readonly _schedule: ProductScheduleSettings;
  private readonly _inventory: ProductInventorySettings;

  public readonly reference: DocumentReference;
  public readonly venue: DocumentReference;

  public events$: Observable<CalendarEvent[]>;
  private events$$: Subscription;
  public events: CalendarEvent[];

  constructor(builder: ProductBuilder) {
    this.reference = builder.reference;
    this.venue = builder.venue;
    this.events$ = builder.events$;
    this.events = [];
    this._shared = new ProductSharedSettings(builder.shared);
    this._schedule = new ProductScheduleSettings(builder.schedule);
    this._advanced = new ProductAdvancedSettings(builder.advanced);
    this._inventory = new ProductInventorySettings(builder.inventory);
    this.events$ == null ? null : this.subscribe();
  }

  get shared(): ProductSharedSettings {
    return this._shared;
  }
  get advanced(): ProductAdvancedSettings {
    return this._advanced;
  }
  get schedule(): ProductScheduleSettings {
    return this._schedule;
  }

  get inventory(): ProductInventorySettings {
    return this._inventory;
  }

  get id(): string {
    return this.reference.id;
  }

  get path(): string {
    return this.reference.path;
  }

  get isActive(): boolean {
    return this._shared.isActive;
  }

  get name(): string {
    return this._shared.name;
  }

  get serviceFee(): number {
    return this._shared.serviceFee;
  }

  get tips(): number[] {
    return this._shared.tips;
  }
  get productType(): ProductType {
    return this._shared.type;
  }
  get productTypeMask(): string {
    return ProductType[this._shared.type];
  }

  get description(): string {
    return this._shared.description;
  }

  get instructions(): string {
    return this._shared.instructions;
  }

  get warning(): string {
    return this._shared.warning;
  }

  get questions(): BasicQuestion[] {
    return this._shared.questions;
  }

  get scheduleType(): ScheduleType {
    return this._schedule.type;
  }

  get scheduleTypeMask(): string {
    return ScheduleType[this._schedule.type];
  }

  toJSON = () => ({
    venue: this.venue,
    shared: this._shared.toJSON(),
    schedule: this._schedule ? this._schedule.toJSON() : null,
    advanced: this._advanced ? this._advanced.toJSON() : null,
    inventory: this._inventory ? this._inventory.toJSON() : null
  });

  //TODO: Write Function
  get status(): ProductStatus {
    //Active : Has Valid Future Date, And showing in App
    //Staged: Has Valid Future Date, not showing in App because its too early to show
    //Expired: Date assigned in the Past
    //Incomplete: No valid date set, or something else missing that is required
    return ProductStatus.Incomplete;
  }

  //TODO: Refactor for settings objects
  get schedulestr(): string {
    if (this.events == null || this.events == [])
      if (this.scheduleType == ScheduleType.CustomDays) return "Next: None";
      else return "None";
    let future_events = this.events.filter(event => event.date > new Date());
    let tf: Timeframe;
    switch (this.scheduleType) {
      case ScheduleType.Continuous:
        tf = new Timeframe(
          firebase.firestore.Timestamp.fromDate(future_events[0].date),
          firebase.firestore.Timestamp.fromDate(
            future_events[future_events.length - 1].date
          )
        );
        return tf.toString();
      case ScheduleType.DayOfWeek:
        tf = new Timeframe(
          firebase.firestore.Timestamp.fromDate(future_events[0].date),
          firebase.firestore.Timestamp.fromDate(
            future_events[future_events.length - 1].date
          )
        );
        return tf.toString() + " " + this._schedule.dow.toString();
      case ScheduleType.CustomDays:
        if (future_events.length == 0 || future_events == null)
          return "Next: None";

        let next = this.ds.getString(future_events[0].date);
        let future = future_events.length - 1;
        if (future == 0) {
          return "Next: " + next + ", Future: None";
        }
        if (future == 1) {
          return "Next: " + next + ", Future: 1 Date";
        } else {
          return "Next: " + next + ", Future: " + future + " Dates";
        }
        break;
    }
  }

  unsubscribe = () => this.events$$.unsubscribe();

  subscribe = () =>
    (this.events$$ = this.events$.subscribe(events => {
      this.events = events;
    }));

  compare = (product: Product): number =>
    this.status != product.status
      ? this.status - product.status
      : this.name < product.name
      ? -1
      : 1;

  get isLineskip(): boolean {
    return this.productType == ProductType.LineSkip;
  }
  get isCover(): boolean {
    return this.productType == ProductType.Cover;
  }
  get isDrink(): boolean {
    return this.productType == ProductType.Drink;
  }
  get isOther(): boolean {
    return this.productType == ProductType.Other;
  }
}
